import React, { useEffect, useState } from 'react';
import { Button, Tooltip } from '@mui/material';
import { useDrawerContext } from 'components/Drawer/draggable-drawer/DraggableDrawer.context';
import { DRAWER_PADDING_OFFSET } from 'components/Drawer/widget-drawer/WidgetDrawer.constants';
import useWorkbook from 'components/ScalarSpreadsheet/utilities/useWorkbook';
import SpreadsheetNote from 'components/SpreadsheetNotes/spreadsheet-note/SpreadsheetNote';
import useNoteSpreadsheet
  from 'components/SpreadsheetNotes/SpreadsheetNoteConfig/UseNoteSpreadsheet/useNoteSpreadsheet';
import {
  UseSpreadsheetNoteTabs
} from 'components/SpreadsheetNotes/SpreadsheetNoteConfig/UseSpreadsheetNoteTabs/useSpreadsheetNoteTabs.props';
import {
  ColumnReferenceType,
  RowReferenceType,
  SpreadsheetNoteCellData,
  SpreadsheetNoteData
} from 'components/SpreadsheetNotes/types';
import { Tab } from 'components/TabsNavigation';
import { EvaluatedCell } from 'models/interfaces/spreadsheet-types/core-types';
import { OnChangeFn } from 'models/interfaces/spreadsheet-types/workbook-types';
import { ApproachActionOption } from 'pages/ValuationsAllocation/components';
import { Ordinate } from 'services/sheet-attribute-manager/models/sheet-attribute-manager.interface';
import { NEGATIVE_PREFIX } from '../../../../pages/Financials/financials-widget-drawer/types';

const useSpreadsheetNoteTabs = ({
  notes,
  actionsAreDisabled,
  handleOnDeleteNote,
  handleMenuClose,
  handleOnAddNote,
  isAttributeMapperEnabled,
  attributeReferenceConfig,
  attributeMapper,
  documentId,
}: UseSpreadsheetNoteTabs) => {
  const [tabs, setTabs] = useState<Tab[]>([]);
  const { spreadsheets } = useNoteSpreadsheet({
    notes,
    isAttributeMapperEnabled,
    attributeReferenceConfig,
    attributeManager: attributeMapper,
    isPreview: !!documentId,
  });

  const { drawerHeight } = useDrawerContext()

  const innerDrawerHeight = drawerHeight - DRAWER_PADDING_OFFSET; // account for padding and tab height

  const { cells, onChange, workbook, workbookContextValue } = useWorkbook(spreadsheets)

  const onNoteChange: OnChangeFn = (c, expr) => {
    onChange(c, expr);
    const cell = c as EvaluatedCell<SpreadsheetNoteCellData>;
    if (
      Array.isArray(expr) &&
      cell.data?.referenceType &&
      cell.data.sheetName &&
      isAttributeMapperEnabled &&
      attributeMapper
    ) {
      const values = expr
        .filter(value => value !== '')
        .map(value => ({
          value,
          direction: value.startsWith(NEGATIVE_PREFIX) ? 'negative' : 'positive',
        })) as Ordinate[];
      const docModPayload = {
        documentReferenceId: documentId,
        sheet: cell.data.sheetName,
      };
      if (cell.data.referenceType === ColumnReferenceType) {
        //   expr is an array of string
        attributeMapper.handleColumnUpdate(docModPayload, cell.columnLegend, values);
        //     call the mapper thing andupdate teh column with the expr
      } else if (cell.data.referenceType === RowReferenceType) {
        //     call the mapper thing and update the row with the expr
        attributeMapper.handleRowUpdate(docModPayload, cell.rowNumber, values);
      }
    }
  };

  useEffect(() => {
    // only allow a single temp note at any given time...
    const hasTempNote = !!notes?.find(i => i.temp_id);
    const noteTabs: Tab[] = spreadsheets?.filter(sheet => sheet.initialObj).map((sheet, i) => {
      const note = sheet.initialObj as SpreadsheetNoteData
      const menuItems = handleOnDeleteNote && handleMenuClose ? [
        <ApproachActionOption<SpreadsheetNoteData, never>
          key={`delete-${note.id ?? note.temp_id}`}
          actionText="Delete"
          argumentsList={[note]}
          tooltipMessage="Delete Spreadsheet Note from the system"
          onClick={() => {
            handleOnDeleteNote({...note, is_deleted: true});
            handleMenuClose();
          }}
        />,
      ] : undefined
      return ({
        content: <SpreadsheetNote
          key={note.id ?? note.temp_id}
          note={sheet}
          parentContainerHeight={innerDrawerHeight}
          isAttributeMapperEnabled={isAttributeMapperEnabled}
          onChange={onNoteChange}
          workbook={workbook}
          cells={cells}
          isDisabled={actionsAreDisabled}/>,
        disabled: actionsAreDisabled,
        id: note.id ? `${note.id}` : `${note.temp_id}`,
        label: note.name || `Spreadsheet Note ${i + 1}`,
        menuItems
      });
    }) ?? [];

    if (handleOnAddNote) {
      noteTabs.push({
        id: 'add-button',
        content: <></>,
        disabled: false,
        isActionTab: true,
        label: (
          <Tooltip title={hasTempNote ? 'Only one pending note allowed' : ''}>
            <span>
              <Button onClick={handleOnAddNote} disabled={hasTempNote || actionsAreDisabled}>
                +
              </Button>
            </span>
          </Tooltip>
        ),
      });
    }

    setTabs(noteTabs);

    // exhaustive dependencies are not needed here and result in degraded performance
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notes, actionsAreDisabled, spreadsheets, cells, drawerHeight]);

  return {
    tabs,
    workbookContextValue,
  };
};

export default useSpreadsheetNoteTabs;